import {
  assetViewSchema,
  type AssetView,
  type AssetVisibility
} from "@seedance/contracts";
import { ApiClientError } from "./client.js";

interface ApiErrorBody {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
  };
}

export interface UploadXhr {
  upload: {
    onprogress?: ((event: ProgressEvent<EventTarget>) => void) | null;
  };
  status: number;
  responseText: string;
  withCredentials: boolean;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
  open(method: string, url: string): void;
  setRequestHeader(name: string, value: string): void;
  send(body: Document | XMLHttpRequestBodyInit | null): void;
  abort(): void;
}

export interface UploadAssetInput {
  file: File;
  projectId?: string;
  visibility?: AssetVisibility;
  onProgress?: (value: number) => void;
  signal?: AbortSignal;
}

export interface UploadTransportOptions {
  xhr?: () => UploadXhr;
  csrfToken: () => string | undefined;
}

export type UploadAsset = (input: UploadAssetInput) => Promise<AssetView>;

function abortError(): DOMException {
  return new DOMException("The upload was aborted.", "AbortError");
}

function apiError(status: number, responseText: string): ApiClientError {
  let body: ApiErrorBody = {};
  try {
    body = JSON.parse(responseText) as ApiErrorBody;
  } catch {
    // A non-JSON error response still becomes a consistent client error.
  }
  return new ApiClientError({
    status,
    code: typeof body.error?.code === "string" ? body.error.code : "REQUEST_FAILED",
    message:
      typeof body.error?.message === "string"
        ? body.error.message
        : "Request could not be completed.",
    requestId:
      typeof body.error?.requestId === "string"
        ? body.error.requestId
        : "unavailable"
  });
}

export function createUploadTransport(
  options: UploadTransportOptions
): UploadAsset {
  const createXhr = options.xhr ?? (() => new XMLHttpRequest());

  return (input) =>
    new Promise<AssetView>((resolve, reject) => {
      const xhr = createXhr();
      let settled = false;

      const finish = (callback: () => void) => {
        if (settled) return;
        settled = true;
        input.signal?.removeEventListener("abort", onSignalAbort);
        callback();
      };
      const onSignalAbort = () => xhr.abort();

      const parameters = new URLSearchParams();
      if (input.projectId) parameters.set("projectId", input.projectId);
      if (input.visibility === "team") parameters.set("visibility", "team");
      xhr.open("POST", `/api/assets${parameters.size ? `?${parameters.toString()}` : ""}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", input.file.type || "application/octet-stream");
      xhr.setRequestHeader("X-File-Name", encodeURIComponent(input.file.name));
      const csrfToken = options.csrfToken();
      if (csrfToken !== undefined) {
        xhr.setRequestHeader("X-CSRF-Token", csrfToken);
      }
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          input.onProgress?.(event.loaded / event.total);
        }
      };
      xhr.onload = () => {
        finish(() => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(assetViewSchema.parse(JSON.parse(xhr.responseText)));
            } catch (error) {
              reject(error);
            }
            return;
          }
          reject(apiError(xhr.status, xhr.responseText));
        });
      };
      xhr.onerror = () => {
        finish(() =>
          reject(
            new ApiClientError({
              status: 0,
              code: "NETWORK_ERROR",
              message: "Network request failed.",
              requestId: "unavailable"
            })
          )
        );
      };
      xhr.onabort = () => finish(() => reject(abortError()));

      if (input.signal?.aborted) {
        finish(() => reject(abortError()));
        return;
      }
      input.signal?.addEventListener("abort", onSignalAbort, { once: true });
      xhr.send(input.file);
    });
}
